import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export const DEFAULT_PAGE_SIZE = 30
export const PAGE_SIZE_OPTIONS = [30, 50, 100, 200, 300, 500, 1000] as const

interface ListPaginationProps {
  page: number
  pageSize: number
  total: number
  loading?: boolean
  showPageNumbers?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

function getVisiblePages(page: number, totalPages: number) {
  return Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((item) => Math.abs(item - page) <= 2 || item === 1 || item === totalPages)
}

export function ListPagination({
  page,
  pageSize,
  total,
  loading = false,
  showPageNumbers = true,
  onPageChange,
  onPageSizeChange,
}: ListPaginationProps) {
  if (total <= 0) return null

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const visiblePages = getVisiblePages(page, totalPages)

  const handlePageSizeChange = (value: string) => {
    onPageChange(1)
    onPageSizeChange(Number(value))
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-muted-foreground">
        显示 {start}-{end} 条，共 {total} 条
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-[118px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} 条/页
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
        >
          <ChevronLeft className="h-4 w-4" />
          上一页
        </Button>
        {showPageNumbers ? (
          visiblePages.map((item, index, array) => {
            const hasGap = index > 0 && item - array[index - 1] > 1
            return (
              <span key={item} className="inline-flex items-center gap-2">
                {hasGap && <span className="text-sm text-muted-foreground">...</span>}
                <Button
                  variant={item === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onPageChange(item)}
                  disabled={loading}
                >
                  {item}
                </Button>
              </span>
            )
          })
        ) : (
          <span className="text-sm text-muted-foreground">
            第 {page} / {totalPages} 页
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
        >
          下一页
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
